import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import {
    SignatureModalActions,
    SignatureModalBox,
    SignatureModalCanvas,
    SignatureModalCanvasWrap,
    SignatureModalHint,
    SignatureModalOverlay,
    SignatureModalTitle,
} from './Roteiros.styles';

const { Button } = CrudStyles;

const STROKE_COLOR = '#111111';
const STROKE_WIDTH = 2.5;
const EXPORT_WIDTH = 800;
const EXPORT_HEIGHT = 400;

/** Coordenadas em pixels CSS — o contexto usa setTransform(dpr), então não escalar por devicePixelRatio aqui. */
function getCanvasPoint(canvas, clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    return {
        x: clientX - rect.left,
        y: clientY - rect.top,
    };
}

function setupCanvasSize(canvas, wrap) {
    const dpr = window.devicePixelRatio || 1;
    const width = wrap.clientWidth;
    if (width <= 0) return null;

    const height = Math.min(240, Math.max(200, width * 0.5));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = STROKE_COLOR;
    ctx.fillStyle = STROKE_COLOR;
    ctx.lineWidth = STROKE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return { ctx, width, height };
}

/**
 * Modal para coletar assinatura e devolver um File PNG com fundo branco.
 *
 * @param {{ open: boolean, onCancel: () => void, onConfirm: (file: File) => void }} props
 */
export default function SignatureCaptureModal({ open, onCancel, onConfirm }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const hasStrokeRef = useRef(false);
    const ctxRef = useRef(null);
    const activePointerIdRef = useRef(null);

    const [hasStroke, setHasStroke] = useState(false);
    const [exporting, setExporting] = useState(false);

    const markHasStroke = useCallback(() => {
        if (hasStrokeRef.current) return;
        hasStrokeRef.current = true;
        setHasStroke(true);
    }, []);

    const resetCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const wrap = wrapRef.current;
        if (!canvas || !wrap) return false;

        const result = setupCanvasSize(canvas, wrap);
        if (!result) return false;

        ctxRef.current = result.ctx;
        drawingRef.current = false;
        activePointerIdRef.current = null;
        hasStrokeRef.current = false;
        setHasStroke(false);
        return true;
    }, []);

    const plotPoint = useCallback(
        (x, y) => {
            const ctx = ctxRef.current;
            if (!ctx) return;
            ctx.beginPath();
            ctx.arc(x, y, STROKE_WIDTH / 2, 0, Math.PI * 2);
            ctx.fill();
            markHasStroke();
        },
        [markHasStroke],
    );

    const beginStroke = useCallback(
        (x, y) => {
            const ctx = ctxRef.current;
            if (!ctx) return;
            drawingRef.current = true;
            ctx.beginPath();
            ctx.moveTo(x, y);
            plotPoint(x, y);
        },
        [plotPoint],
    );

    const continueStroke = useCallback(
        (x, y) => {
            if (!drawingRef.current) return;
            const ctx = ctxRef.current;
            if (!ctx) return;
            ctx.lineTo(x, y);
            ctx.stroke();
            markHasStroke();
        },
        [markHasStroke],
    );

    const endStroke = useCallback(() => {
        drawingRef.current = false;
        activePointerIdRef.current = null;
    }, []);

    useEffect(() => {
        if (!open) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        let cancelled = false;
        let resizeObserver;

        const ensureCanvasReady = () => {
            if (cancelled) return;
            if (resetCanvas()) return;
            requestAnimationFrame(ensureCanvasReady);
        };

        requestAnimationFrame(ensureCanvasReady);

        const wrap = wrapRef.current;
        if (wrap && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                if (hasStrokeRef.current) return;
                resetCanvas();
            });
            resizeObserver.observe(wrap);
        }

        const handleResize = () => {
            if (hasStrokeRef.current) return;
            resetCanvas();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            cancelled = true;
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
        };
    }, [open, resetCanvas]);

    useEffect(() => {
        if (!open) return undefined;

        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const isActivePointer = (event) =>
            activePointerIdRef.current == null || event.pointerId === activePointerIdRef.current;

        const handlePointerDown = (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (activePointerIdRef.current != null && event.pointerId !== activePointerIdRef.current) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            activePointerIdRef.current = event.pointerId;
            try {
                canvas.setPointerCapture(event.pointerId);
            } catch {
                // captura indisponível em alguns browsers
            }

            const { x, y } = getCanvasPoint(canvas, event.clientX, event.clientY);
            beginStroke(x, y);
        };

        const handlePointerMove = (event) => {
            if (!drawingRef.current || !isActivePointer(event)) return;

            event.preventDefault();
            event.stopPropagation();

            const { x, y } = getCanvasPoint(canvas, event.clientX, event.clientY);
            continueStroke(x, y);
        };

        const finishPointer = (event) => {
            if (!isActivePointer(event)) return;

            event.preventDefault();
            event.stopPropagation();

            try {
                if (canvas.hasPointerCapture(event.pointerId)) {
                    canvas.releasePointerCapture(event.pointerId);
                }
            } catch {
                // pointer já liberado
            }
            endStroke();
        };

        const passiveFalse = { passive: false };

        const blockTouchScroll = (event) => {
            if (event.cancelable) {
                event.preventDefault();
            }
        };

        canvas.addEventListener('pointerdown', handlePointerDown, passiveFalse);
        canvas.addEventListener('pointermove', handlePointerMove, passiveFalse);
        canvas.addEventListener('pointerup', finishPointer, passiveFalse);
        canvas.addEventListener('pointercancel', finishPointer, passiveFalse);
        canvas.addEventListener('touchstart', blockTouchScroll, passiveFalse);
        canvas.addEventListener('touchmove', blockTouchScroll, passiveFalse);

        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            canvas.removeEventListener('pointermove', handlePointerMove);
            canvas.removeEventListener('pointerup', finishPointer);
            canvas.removeEventListener('pointercancel', finishPointer);
            canvas.removeEventListener('touchstart', blockTouchScroll);
            canvas.removeEventListener('touchmove', blockTouchScroll);
        };
    }, [open, beginStroke, continueStroke, endStroke]);

    const handleClear = () => {
        resetCanvas();
    };

    const handleConfirm = async () => {
        const source = canvasRef.current;
        if (!source || !hasStrokeRef.current) return;

        setExporting(true);
        try {
            const offscreen = document.createElement('canvas');
            offscreen.width = EXPORT_WIDTH;
            offscreen.height = EXPORT_HEIGHT;
            const ctx = offscreen.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
            ctx.drawImage(source, 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

            const blob = await new Promise((resolve, reject) => {
                offscreen.toBlob(
                    (result) => {
                        if (result) resolve(result);
                        else reject(new Error('Não foi possível gerar a imagem da assinatura.'));
                    },
                    'image/png',
                );
            });

            const filename = `assinatura-${Date.now()}.png`;
            const file = new File([blob], filename, { type: 'image/png' });
            onConfirm(file);
        } finally {
            setExporting(false);
        }
    };

    if (!open) return null;

    return (
        <SignatureModalOverlay
            role="dialog"
            aria-modal="true"
            aria-labelledby="signature-modal-title"
            onClick={(e) => e.target === e.currentTarget && !exporting && onCancel?.()}
        >
            <SignatureModalBox onClick={(e) => e.stopPropagation()}>
                <SignatureModalTitle id="signature-modal-title">Assinatura</SignatureModalTitle>
                <SignatureModalHint>Peça para o recebedor assinar no espaço abaixo.</SignatureModalHint>
                <SignatureModalCanvasWrap ref={wrapRef}>
                    <SignatureModalCanvas ref={canvasRef} aria-label="Área para assinatura" />
                </SignatureModalCanvasWrap>
                <SignatureModalActions>
                    <Button type="button" variant="outline" disabled={exporting} onClick={onCancel}>
                        Cancelar
                    </Button>
                    <Button type="button" variant="outline" disabled={exporting} onClick={handleClear}>
                        Limpar
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        disabled={!hasStroke || exporting}
                        onClick={handleConfirm}
                    >
                        {exporting ? 'Gerando…' : 'Usar assinatura'}
                    </Button>
                </SignatureModalActions>
            </SignatureModalBox>
        </SignatureModalOverlay>
    );
}
